import React, { useEffect, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { ReactComponent as HandshakeIcon } from '../icons/prequal_handshake.svg';
import { ReactComponent as MedalIcon } from '../icons/prequal_medal.svg';
import { pushToDataLayer } from '../utils/gtm';
import { withExpVariant } from '../utils/kamExpVariant';
import { sendKameleoonGoalConversion } from '../utils/lendAPIKameleoonHelper';
import {
  BOAT_TRADER_SOURCE_ID,
  BOATTRADER_BOAT_LOANS_URL,
  DEFAULT_THEME,
  PUBLIC_URL,
} from '../constants';
import { LoanPurpose, Source } from '../schema';
import { AppFooter } from '../loan/components/AppFooter/AppFooter';
import styles from './PreQualLandingPage.module.scss';

export const PREQUAL_LANDING_FUNNEL = 'lendapi combined';
export const PREQUAL_LANDING_DETAILED_PAGE_NAME = 'prequalify boat loan landing';
export const PREQUAL_LANDING_SITE_SECTION = 'trident finance';
export const PREQUAL_LANDING_CTA_ACTION_LABEL = 'get prequalified';
export const PREQUAL_LANDING_CTA_ACTION_LOCATION = 'main content CTA';
export const PREQUAL_LANDING_FAQ_ACTION_LABEL = 'faq';
export const PREQUAL_LANDING_FAQ_ACTION_LOCATION = 'content bottom help text';
export const PREQUAL_LANDING_PHONE_ACTION_LABEL = 'phone call support';
export const PREQUAL_LANDING_PHONE_ACTION_LOCATION = 'content bottom help text';
export const PREQUAL_LANDING_FAQ_HREF = `${BOATTRADER_BOAT_LOANS_URL}#boat-loans-faq`;
export const PREQUAL_LANDING_KAM_PRODUCT_TYPE = 'combined';
// detailedPageName values sent by portal-react-boattrader's CTA buttons, identifying which
// marketing page a click came from (BDP/SRP clicks lack this param entirely).
export const PREQUAL_LANDING_BOAT_LOANS_DETAILED_PAGE_NAME = 'finance';
export const PREQUAL_LANDING_RATES_DETAILED_PAGE_NAME = 'boat loan rates';
export const PREQUAL_LANDING_CALCULATOR_DETAILED_PAGE_NAME = 'boat loan calculator';

// Origins that skip this landing page and go straight to the funnel. Shared by
// usePreQualLandingAutoAdvance and PreQualLandingRoute so they can't drift apart.
export const PREQUAL_LANDING_AUTO_ADVANCE_ORIGINS: readonly string[] = Object.freeze([
  PREQUAL_LANDING_BOAT_LOANS_DETAILED_PAGE_NAME,
  PREQUAL_LANDING_RATES_DETAILED_PAGE_NAME,
  PREQUAL_LANDING_CALCULATOR_DETAILED_PAGE_NAME,
]);

export const isPreQualLandingAutoAdvanceOrigin = (detailedPageName: string | null): boolean =>
  !!detailedPageName && PREQUAL_LANDING_AUTO_ADVANCE_ORIGINS.includes(detailedPageName);
export const PREQUAL_LANDING_PAGEVIEW_KAM_GOAL = 'custom-lendapi-combined-prequal-landing-pageview';
export const PREQUAL_LANDING_CTA_KAM_GOAL = 'custom-lendapi-combined-prequal-landing-cta-click';
// Kept in sync with PREQUAL_LANDING_STATIC_SOURCE.marinePhone below so the
// number shown in the body text matches the number rendered in the footer.
export const PREQUAL_LANDING_PHONE_DISPLAY = '(888) 356-6005';
export const PREQUAL_LANDING_PHONE_HREF = 'tel:+18883566005';

// Static BoatTrader source passed to <AppFooter sourceOverride> so the
// pre-qual landing page renders the exact same funnel footer without
// depending on Firebase config or the loan store. Kept in sync with the
// BoatTrader source in dynamic-app/src/scripts/initdata.ts (id 101458).
// theme.logo is the white-on-dark BoatTrader | Trident SVG used in the
// footer against the dark-navy `theme.colors.footer` background.
export const PREQUAL_LANDING_STATIC_SOURCE: Source = {
  id: BOAT_TRADER_SOURCE_ID,
  displayName: 'Trident Funding LLC',
  name: 'Boat Trader',
  products: [LoanPurpose.boat],
  marinePhone: '888-356-6005',
  marineFax: '510-356-2839',
  operationHoursMarine: '',
  sub: [],
  hostname: 'www.boattrader.com',
  theme: {
    ...DEFAULT_THEME,
    logo: `${PUBLIC_URL}/static/trident/boattrader-trident-logo.svg`,
    colors: {
      ...DEFAULT_THEME.colors,
      footer: '#0B3F65',
    },
  },
};

export const trackPreQualLandingPageview = (): void => {
  pushToDataLayer(
    'pageview',
    withExpVariant({
      detailed_page_name: PREQUAL_LANDING_DETAILED_PAGE_NAME,
      site_section: PREQUAL_LANDING_SITE_SECTION,
      funnel: PREQUAL_LANDING_FUNNEL,
    })
  );
  sendKameleoonGoalConversion(PREQUAL_LANDING_PAGEVIEW_KAM_GOAL, PREQUAL_LANDING_KAM_PRODUCT_TYPE);
};

export const trackPreQualLandingCtaClick = (): void => {
  pushToDataLayer(
    'link_click',
    withExpVariant({
      action_type: 'cta',
      action_label: PREQUAL_LANDING_CTA_ACTION_LABEL,
      action_location: PREQUAL_LANDING_CTA_ACTION_LOCATION,
      detailed_page_name: PREQUAL_LANDING_DETAILED_PAGE_NAME,
      listing_id: '',
    })
  );
  sendKameleoonGoalConversion(PREQUAL_LANDING_CTA_KAM_GOAL, PREQUAL_LANDING_KAM_PRODUCT_TYPE);
};

export const trackPreQualLandingFaqClick = (): void => {
  pushToDataLayer(
    'link_click',
    withExpVariant({
      action_type: 'navigation',
      action_label: PREQUAL_LANDING_FAQ_ACTION_LABEL,
      action_location: PREQUAL_LANDING_FAQ_ACTION_LOCATION,
      detailed_page_name: PREQUAL_LANDING_DETAILED_PAGE_NAME,
      listing_id: '',
    })
  );
};

export const trackPreQualLandingPhoneClick = (): void => {
  pushToDataLayer(
    'link_click',
    withExpVariant({
      action_type: 'external',
      action_label: PREQUAL_LANDING_PHONE_ACTION_LABEL,
      action_location: PREQUAL_LANDING_PHONE_ACTION_LOCATION,
      detailed_page_name: PREQUAL_LANDING_DETAILED_PAGE_NAME,
      listing_id: '',
    })
  );
};

type Props = {
  onGetPreQualified: () => void;
};

// Shared by V1 and V2 of this landing page so both stay behind the exact
// same boat-loans-origin auto-advance and modal chrome-hiding rules --
// duplicating this per-version previously let V2 silently drift out of sync
// with V1's auto-advance behavior (TRIDENT-1001).
export const usePreQualLandingAutoAdvance = (
  onGetPreQualified: () => void
): { hideChrome: boolean; shouldAutoAdvance: boolean } => {
  const searchParams = new URLSearchParams(window.location.search);
  const hideChrome = searchParams.get('embedded') === 'modal';
  // Independent of hideChrome/embedded=modal so the skip works in the new-tab bucket too.
  const shouldAutoAdvance = isPreQualLandingAutoAdvanceOrigin(searchParams.get('detailedPageName'));

  const autoAdvanceTriggered = useRef(false);

  useEffect(() => {
    // The auto-advance path fires its own pageview from the useLayoutEffect
    // below instead, synchronously before the navigation call -- this plain
    // useEffect is scheduled asynchronously (after paint), so on that path it
    // could lose the race against window.location.assign unloading the page
    // before it ever runs.
    if (!shouldAutoAdvance) {
      trackPreQualLandingPageview();
    }
  }, [shouldAutoAdvance]);

  useLayoutEffect(() => {
    if (shouldAutoAdvance && !autoAdvanceTriggered.current) {
      autoAdvanceTriggered.current = true;
      // The user still passed through this step of the funnel (it just
      // isn't rendered), so skipping this would understate it in funnel
      // analytics. Fired here, synchronously and before onGetPreQualified's
      // navigation, rather than from a plain useEffect, so it cannot be
      // skipped by the ensuing page unload.
      trackPreQualLandingPageview();
      onGetPreQualified();
    }
  }, [shouldAutoAdvance, onGetPreQualified]);

  return { hideChrome, shouldAutoAdvance };
};

const PreQualLandingPage: React.FC<Props> = ({ onGetPreQualified }) => {
  const { hideChrome, shouldAutoAdvance } = usePreQualLandingAutoAdvance(onGetPreQualified);

  const handleCtaClick = (): void => {
    trackPreQualLandingCtaClick();
    onGetPreQualified();
  };

  // Renders nothing on the very first pass so there is no landing-page
  // content to flash before handoff — the redirect itself fires from
  // usePreQualLandingAutoAdvance's useLayoutEffect, after this
  // (already-empty) paint.
  if (shouldAutoAdvance) {
    return null;
  }

  return (
    <div className={styles.landingRoot} data-testid="prequal-landing-page">
      {!hideChrome && (
        <header className={styles.header}>
          <img
            alt="Boat Trader | Trident Funding"
            className={styles.headerLogo}
            src={`${PUBLIC_URL}/static/trident/boattrader-trident-logo-blue.png`}
          />
        </header>
      )}

      <section
        className={classNames(styles.heroSection, { [styles.heroSectionHideChrome]: hideChrome })}
        data-testid="prequal-landing-hero"
      >
        <span className={styles.badge}>MARINE FINANCE</span>

        <div className={styles.heroHeadingWrap}>
          <h1 className={styles.heroHeading}>
            Get <span className={styles.heroHeadingAccent}>Prequalified</span> for a Boat Loan
          </h1>
          <p className={styles.heroSubheading}>
            Pre-qualification takes 3 minutes, uses a soft credit check and gives you real approval
            odds, so you can shop with confidence.
          </p>
        </div>

        <button
          className={styles.ctaButton}
          data-testid="prequal-landing-cta"
          onClick={handleCtaClick}
          type="button"
        >
          Get Pre-qualified
        </button>
      </section>

      <section className={styles.whySection}>
        <div className={styles.whyInner}>
          <div className={styles.whyContent}>
            <div>
              <h2 className={styles.whyHeading}>
                Why get <span className={styles.whyHeadingAccent}>pre-qualified?</span>
              </h2>
              <p className={styles.whySubheading}>
                Everything you need to buy with clarity and zero pressure.
              </p>
            </div>

            <div className={styles.cardRow}>
              <div className={styles.card}>
                <div className={styles.cardIcon}>
                  <MedalIcon />
                </div>
                <h3 className={styles.cardTitle} data-testid="prequal-landing-card-title">
                  No Credit Impact
                </h3>
                <p className={styles.cardText}>
                  A soft inquiry only, our rate check won&apos;t affect your credit score in any
                  way.
                </p>
              </div>

              <div className={styles.card}>
                <div className={styles.cardIcon}>
                  <HandshakeIcon />
                </div>
                <h3 className={styles.cardTitle} data-testid="prequal-landing-card-title">
                  Expert-Led
                </h3>
                <p className={styles.cardText}>
                  Marine finance specialists guide you from pre-qual through closing.
                </p>
              </div>
            </div>
          </div>

          <div aria-hidden="true" className={styles.phoneMockupWrapper}>
            <img
              alt=""
              className={styles.phoneMockupImg}
              src={`${PUBLIC_URL}/static/trident/prequal-results-phone-mockup.png`}
            />
          </div>
        </div>

        <div className={styles.questionsBlock}>
          <span className={styles.questionsPrompt}>Questions?</span>
          <span className={styles.questionsInfo}>
            Visit our{' '}
            <a
              className={styles.questionsLink}
              data-testid="prequal-landing-faq-link"
              href={PREQUAL_LANDING_FAQ_HREF}
              onClick={trackPreQualLandingFaqClick}
              rel="noopener noreferrer"
              target="_blank"
            >
              FAQ
            </a>{' '}
            or call{' '}
            <a
              className={styles.questionsLink}
              data-testid="prequal-landing-phone-link"
              href={PREQUAL_LANDING_PHONE_HREF}
              onClick={trackPreQualLandingPhoneClick}
            >
              {PREQUAL_LANDING_PHONE_DISPLAY}
            </a>
          </span>
        </div>
      </section>

      {!hideChrome && <AppFooter sourceOverride={PREQUAL_LANDING_STATIC_SOURCE} />}
    </div>
  );
};

export default PreQualLandingPage;
